<template>
  <div class="relative z-200">
    <Overlay
      :visible="isDateRequestSidebarOpen"
      @click="handleDateRequestSidebarClose"
    />
    <Drawer
      ref="dateRequestSidebarRef"
      class="w-full sm:w-[600px] p-4 pt-0 pb-32 flex flex-col bg-gray-50!"
      :is-open="isDateRequestSidebarOpen"
      @click:close="handleDateRequestSidebarClose"
    >
      <template #header>
        <div
          class="title flex items-center"
          :class="{
            'title--completed':
              dateRequestCompleted || privateDateRequestCompleted,
          }"
        >
          <button
            v-if="
              showMatchingDatesLayer ||
              dateRequestCompleted ||
              privateDateRequestCompleted
            "
            class="mr-3 p-1 hover:bg-gray-200 rounded-full transition-colors"
            @click="
              dateRequestCompleted || privateDateRequestCompleted
                ? resetDateRequestEventRequestForms()
                : handleBackToForm()
            "
          >
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span v-if="!privateDateRequestCompleted" class="mx-auto">
            {{
              showMatchingDatesLayer || dateRequestCompleted
                ? t('dateRequest.availableDates')
                : t('dateRequest.sidebarTitle')
            }}
          </span>
        </div>
      </template>
      <div class="flex flex-col justify-between flex-1">
        <LazyDateRequestForm
          v-if="
            isDateRequestSidebarOpen &&
            !dateRequestCompleted &&
            !privateDateRequestCompleted
          "
          ref="dateRequestFormRef"
          @on-complete-request="handleDateRequestComplete"
          @on-complete-private-request="handlePrivateDateRequestComplete"
          @on-close-date-request="handleDateRequestSidebarClose"
        />
        <LazyDateRequestSuccess
          v-if="dateRequestCompleted || privateDateRequestCompleted"
          :date-request-id="dateRequestId"
          :private-request="privateDateRequestCompleted"
          @on-close-date-request="resetDateRequestEventRequestForms"
        />
      </div>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import {
  isMobileDrawerQueryEnabled,
  useDrawerQueryNavigation,
} from '~/composables/useDrawerQueryNavigation'

const dateRequestSidebarRef = ref(null)
const dateRequestFormRef = ref(null)

const {
  toggleDateRequestSidebar,
  closeDateRequestSidebar,
  isDateRequestSidebarOpen,
} = useUiState()
const { currentDrawerStep, pushDrawerStepQuery, popDrawerStepQuery } =
  useDrawerQueryNavigation()
const { t } = useI18n()
const dateRequestCompleted = ref(false)
const privateDateRequestCompleted = ref(false)
const dateRequestId = ref<string | undefined>()
const usesDrawerQuery = isMobileDrawerQueryEnabled()

const showMatchingDatesLayer = computed(() => {
  return (dateRequestFormRef.value as any)?.showMatchingDatesLayer || false
})

const closeMatchingDatesLayer = () => {
  ;(
    dateRequestFormRef.value as { closeMatchingDatesLayer?: () => void } | null
  )?.closeMatchingDatesLayer?.()
}

const handleDateRequestComplete = (id?: string) => {
  dateRequestId.value = id
  dateRequestCompleted.value = true
}

const handlePrivateDateRequestComplete = (id?: string) => {
  dateRequestId.value = id
  privateDateRequestCompleted.value = true
}

const handleDateRequestSidebarClose = () => {
  closeDateRequestSidebar()
  resetDateRequestEventRequestForms()
}

const handleBackToForm = () => {
  if (usesDrawerQuery) {
    popDrawerStepQuery()
    return
  }

  closeMatchingDatesLayer()
}

const resetDateRequestEventRequestForms = () => {
  privateDateRequestCompleted.value = false
  dateRequestCompleted.value = false
  dateRequestId.value = undefined
}

const scrollDateRequestDrawerToTop = () => {
  nextTick(() => {
    const root = (dateRequestSidebarRef.value as { $el?: HTMLElement } | null)
      ?.$el
    if (root && typeof root.scrollTop === 'number') {
      root.scrollTop = 0
    }
  })
}

provide('resetDateRequestEventRequestForms', resetDateRequestEventRequestForms)
provide('scrollDateRequestDrawerToTop', scrollDateRequestDrawerToTop)

watch(
  [dateRequestCompleted, privateDateRequestCompleted],
  ([completed, privateCompleted]) => {
    if (completed || privateCompleted) {
      scrollDateRequestDrawerToTop()
    }
  }
)

watch(
  // the form is lazy-loaded, so re-run once its ref resolves on first open
  [currentDrawerStep, isDateRequestSidebarOpen, dateRequestFormRef],
  ([step, isOpen]) => {
    if (!usesDrawerQuery || !isOpen) return

    nextTick(() => {
      if (step === 'matching-dates') {
        ;(
          dateRequestFormRef.value as {
            openMatchingDatesLayer?: () => void
          } | null
        )?.openMatchingDatesLayer?.()
        return
      }

      closeMatchingDatesLayer()
    })
  },
  { immediate: true }
)

watch(showMatchingDatesLayer, (isOpen) => {
  if (!usesDrawerQuery || !isDateRequestSidebarOpen.value) return

  if (isOpen && currentDrawerStep.value !== 'matching-dates') {
    pushDrawerStepQuery('matching-dates')
  }
})

defineExpose({ toggleDateRequestSidebar })
</script>
<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.title {
  @apply h-14 flex items-center text-lg font-medium text-[#666971] mb-[-1rem];

  &--completed {
    @apply border-b border-gray-200 -mx-4 px-4;
  }
}
</style>
